import { ajv } from '@cibo/core'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { AutoFormProps } from 'uniforms'
import { AutoForm } from 'uniforms-mui'
import { CiboAutoFormContext } from './CiboAutoFormContext'
import { buildRequiredFieldsLookup } from './requiredFields/collectRequiredFields'

// Normal validator helper
function createValidator(schema: any) {
  const validate = ajv.compile(schema)
  return (model: any) => {
    const errors: Record<string, string> = {}
    validate(model)
    if (validate.errors) {
      for (const err of validate.errors) {
        const path = err.instancePath.slice(1) // remove leading "/"
        errors[path] = err.message || 'Error'
      }
    }
    return errors
  }
}

type CiboAutoFormProps = Partial<AutoFormProps<any>> & {
  warningSchema?: any
  renderDisplay?: (config: { field: any; itemProps: any }) => ReactNode
}

export function CiboAutoForm({
  model,
  schema,
  warningSchema,
  renderDisplay,
  ...props
}: CiboAutoFormProps) {
  const [warnings, setWarnings] = useState<Record<string, string>>({})

  const warningValidator = useMemo(() => {
    return warningSchema && createValidator(warningSchema)
  }, [warningSchema])

  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const touch = useCallback((name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }))
  }, [])

  // Compute required fields dynamically based on schema and model
  const requiredFields = useMemo(() => {
    // Handle both raw schema and JSONSchemaBridge
    const rawSchema = (schema as any)?.schema || schema

    return buildRequiredFieldsLookup(rawSchema, model)
  }, [schema, model])

  const isFieldRequired = useCallback(
    (name: string) => requiredFields.get(name) ?? false,
    [requiredFields]
  )

  return (
    <CiboAutoFormContext.Provider
      value={{ renderDisplay, warnings, touch, touched, isFieldRequired }}
    >
      <AutoForm
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        schema={schema!}
        model={model}
        {...props}
        onChangeModel={(newModel: any) => {
          const warningResults = warningValidator?.(newModel)
          setWarnings(warningResults)

          // @ts-ignore This has been working but type seems wrong. Need to check.
          props.onChangeModel?.(newModel)
        }}
      >
        {props.children}
      </AutoForm>
    </CiboAutoFormContext.Provider>
  )
}
